/**
 * RoleTemplateEditorPanel - Role Template Creation and Editing
 *
 * FERP CRITICAL: Template authoring and management
 *
 * Features:
 * - Create new role templates
 * - Edit existing templates (metadata only)
 * - Configure role permissions
 * - Set compliance frameworks
 * - Categorize templates
 * - Validation and preview
 * - Complete audit trail
 *
 * Architecture Principles:
 * - Data-driven (all operations through RoleTemplateRepository)
 * - Backend ID generation (Snowflake IDs)
 * - Repository pattern for persistence
 * - Immutable audit trails
 * - Multi-tenant isolation
 * - Form validation
 *
 * GitHub Issue: #337
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Group,
  Text,
  Badge,
  Button,
  TextInput,
  Textarea,
  Select,
  MultiSelect,
  Card,
  Alert,
  Divider,
  Title,
  ActionIcon,
  Tooltip,
  Paper,
  Code,
} from '@mantine/core';
import {
  IconDeviceFloppy,
  IconX,
  IconAlertCircle,
  IconCheck,
  IconPlus,
  IconTrash,
  IconInfoCircle,
} from '@tabler/icons-react';
import { useApolloClient } from '@apollo/client/react';
import { ApolloClient } from '@apollo/client';
import {
  RoleTemplate,
  RoleTemplateRepository,
  createRoleTemplateRepository,
  CreateRoleTemplateInput,
  TemplateRoleDefinitionInput,
} from '../data/repositories/RoleTemplateRepository';

export interface RoleTemplateEditorPanelProps {
  /** Tenant ID for multi-tenant isolation */
  tenantId: string;
  /** Template to edit (null for create mode) */
  template: RoleTemplate | null;
  /** Callback when template is saved */
  onSave?: (templateId: string) => void;
  /** Callback when editor is cancelled */
  onCancel?: () => void;
}

/**
 * Role Template Editor Panel
 *
 * Template creation and editing interface
 *
 * FERP Architecture Compliance:
 * - ✅ Data-driven (operations through RoleTemplateRepository)
 * - ✅ Backend Snowflake ID generation
 * - ✅ Repository pattern
 * - ✅ Immutable audit trails
 * - ✅ Multi-tenant isolation
 * - ✅ Complete validation
 */
export function RoleTemplateEditorPanel({
  tenantId,
  template,
  onSave,
  onCancel,
}: RoleTemplateEditorPanelProps) {
  const apolloClient = useApolloClient() as ApolloClient;
  const repository = React.useMemo(
    () => createRoleTemplateRepository(apolloClient),
    [apolloClient]
  );

  const isEditMode = template !== null;

  // Form state
  const [templateName, setTemplateName] = useState(template?.templateName ?? '');
  const [displayName, setDisplayName] = useState(template?.displayName ?? '');
  const [description, setDescription] = useState(template?.description ?? '');
  const [category, setCategory] = useState<string | null>(
    template?.category ?? null
  );
  const [complianceFrameworks, setComplianceFrameworks] = useState<string[]>(
    template?.complianceFrameworks ?? []
  );

  // Role definition state
  const [roleName, setRoleName] = useState(
    template?.roleDefinition.roleName ?? ''
  );
  const [roleDisplayName, setRoleDisplayName] = useState(
    template?.roleDefinition.displayName ?? ''
  );
  const [roleDescription, setRoleDescription] = useState(
    template?.roleDefinition.description ?? ''
  );
  const [scopes, setScopes] = useState<string[]>(
    template?.roleDefinition.scopes ?? []
  );
  const [capabilities, setCapabilities] = useState<string[]>(
    template?.roleDefinition.capabilities ?? []
  );
  const [inheritsFrom, setInheritsFrom] = useState<string[]>(
    template?.roleDefinition.inheritsFrom ?? []
  );

  // Add scope/capability input
  const [scopeInput, setScopeInput] = useState('');
  const [capabilityInput, setCapabilityInput] = useState('');
  const [inheritInput, setInheritInput] = useState('');

  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Validate form
  const validateForm = (): boolean => {
    const errors: string[] = [];

    // Template metadata
    if (!templateName.trim()) errors.push('Template name is required');
    if (!displayName.trim()) errors.push('Display name is required');
    if (!description.trim()) errors.push('Description is required');
    if (!category) errors.push('Category is required');

    // Role definition
    if (!roleName.trim()) errors.push('Role name is required');
    if (!roleDisplayName.trim()) errors.push('Role display name is required');
    if (scopes.length === 0) errors.push('At least one scope is required');
    if (capabilities.length === 0)
      errors.push('At least one capability is required');

    // Name format validation
    if (templateName && !/^[a-z][a-z0-9_-]*$/.test(templateName)) {
      errors.push(
        'Template name must start with lowercase letter and contain only lowercase, numbers, hyphens, and underscores'
      );
    }

    if (roleName && !/^[a-z][a-z0-9_-]*$/.test(roleName)) {
      errors.push(
        'Role name must start with lowercase letter and contain only lowercase, numbers, hyphens, and underscores'
      );
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setError(null);

    try {
      if (isEditMode && template) {
        // Edit mode: Only update metadata
        await repository.updateTemplate(template.templateId, tenantId, {
          displayName,
          description,
          category: category!,
        });

        if (onSave) {
          onSave(template.templateId);
        }
      } else {
        // Create mode: Full template creation
        const roleDefinition: TemplateRoleDefinitionInput = {
          roleName,
          displayName: roleDisplayName,
          description: roleDescription || undefined,
          scopes,
          capabilities,
          inheritsFrom,
        };

        const input: CreateRoleTemplateInput = {
          tenantId,
          templateName,
          displayName,
          description,
          category: category!,
          complianceFrameworks,
          roleDefinition,
        };

        const created = await repository.createTemplate(input);

        if (onSave) {
          onSave(created.templateId);
        }
      }
    } catch (err) {
      setError(err as Error);
    } finally {
      setSaving(false);
    }
  };

  // Add scope
  const handleAddScope = () => {
    if (scopeInput.trim() && !scopes.includes(scopeInput.trim())) {
      setScopes([...scopes, scopeInput.trim()]);
      setScopeInput('');
    }
  };

  // Add capability
  const handleAddCapability = () => {
    if (capabilityInput.trim() && !capabilities.includes(capabilityInput.trim())) {
      setCapabilities([...capabilities, capabilityInput.trim()]);
      setCapabilityInput('');
    }
  };

  // Add inherit
  const handleAddInherit = () => {
    if (inheritInput.trim() && !inheritsFrom.includes(inheritInput.trim())) {
      setInheritsFrom([...inheritsFrom, inheritInput.trim()]);
      setInheritInput('');
    }
  };

  return (
    <Box>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Title order={3}>
            {isEditMode ? 'Edit Template' : 'Create Role Template'}
          </Title>

          <Group>
            <Button variant="light" onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={handleSave}
              loading={saving}
            >
              {isEditMode ? 'Save Changes' : 'Create Template'}
            </Button>
          </Group>
        </Group>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Validation Errors"
            color="red"
          >
            <Stack gap="xs">
              {validationErrors.map((err, idx) => (
                <Text key={idx} size="sm">
                  • {err}
                </Text>
              ))}
            </Stack>
          </Alert>
        )}

        {/* Error Alert */}
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            onClose={() => setError(null)}
            withCloseButton
          >
            {error.message}
          </Alert>
        )}

        {/* Edit Mode Warning */}
        {isEditMode && (
          <Alert icon={<IconInfoCircle size={16} />} title="Edit Mode" color="blue">
            You can only edit metadata (display name, description, category).
            Role definition is immutable after creation.
          </Alert>
        )}

        {/* Template Metadata Section */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Title order={4}>Template Metadata</Title>

            <TextInput
              label="Template Name"
              placeholder="e.g., healthcare-physician"
              value={templateName}
              onChange={(e) => setTemplateName(e.currentTarget.value)}
              disabled={isEditMode}
              required
              description="Unique identifier (lowercase, hyphens, underscores)"
            />

            <TextInput
              label="Display Name"
              placeholder="e.g., Healthcare Physician"
              value={displayName}
              onChange={(e) => setDisplayName(e.currentTarget.value)}
              required
              description="Human-readable name"
            />

            <Textarea
              label="Description"
              placeholder="Describe the purpose and use case for this template..."
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
              minRows={3}
              required
              description="Detailed explanation of template purpose"
            />

            <Select
              label="Category"
              placeholder="Select category"
              value={category}
              onChange={setCategory}
              data={[
                { value: 'healthcare', label: 'Healthcare' },
                { value: 'financial', label: 'Financial' },
                { value: 'operational', label: 'Operational' },
                { value: 'compliance', label: 'Compliance' },
                { value: 'custom', label: 'Custom' },
              ]}
              required
              description="Template category for organization"
            />

            <MultiSelect
              label="Compliance Frameworks"
              placeholder="Select applicable frameworks"
              data={repository.getComplianceFrameworks()}
              value={complianceFrameworks}
              onChange={setComplianceFrameworks}
              description="Regulatory frameworks this template supports"
            />
          </Stack>
        </Card>

        {/* Role Definition Section */}
        {!isEditMode && (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Stack gap="md">
              <Title order={4}>Role Definition</Title>

              <Alert icon={<IconInfoCircle size={16} />} color="blue">
                This configuration is immutable after template creation
              </Alert>

              <TextInput
                label="Role Name"
                placeholder="e.g., physician"
                value={roleName}
                onChange={(e) => setRoleName(e.currentTarget.value)}
                required
                description="Unique role identifier (lowercase, hyphens, underscores)"
              />

              <TextInput
                label="Role Display Name"
                placeholder="e.g., Physician"
                value={roleDisplayName}
                onChange={(e) => setRoleDisplayName(e.currentTarget.value)}
                required
                description="Human-readable role name"
              />

              <Textarea
                label="Role Description"
                placeholder="Describe the role's responsibilities..."
                value={roleDescription}
                onChange={(e) => setRoleDescription(e.currentTarget.value)}
                minRows={2}
                description="Optional detailed role description"
              />

              {/* Scopes */}
              <Box>
                <Text fw={500} size="sm" mb="xs">
                  Scopes *
                </Text>
                <Text size="xs" c="dimmed" mb="md">
                  Permission scopes granted by this role
                </Text>

                <Group mb="sm">
                  <TextInput
                    placeholder="e.g., patient:read"
                    value={scopeInput}
                    onChange={(e) => setScopeInput(e.currentTarget.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddScope();
                      }
                    }}
                    style={{ flex: 1 }}
                  />
                  <Button
                    leftSection={<IconPlus size={16} />}
                    onClick={handleAddScope}
                  >
                    Add
                  </Button>
                </Group>

                <Group gap="xs">
                  {scopes.map((scope) => (
                    <Badge
                      key={scope}
                      color="blue"
                      variant="light"
                      rightSection={
                        <ActionIcon
                          size="xs"
                          color="blue"
                          variant="transparent"
                          onClick={() =>
                            setScopes(scopes.filter((s) => s !== scope))
                          }
                        >
                          <IconX size={10} />
                        </ActionIcon>
                      }
                    >
                      {scope}
                    </Badge>
                  ))}
                  {scopes.length === 0 && (
                    <Text size="sm" c="dimmed">
                      No scopes added
                    </Text>
                  )}
                </Group>
              </Box>

              {/* Capabilities */}
              <Box>
                <Text fw={500} size="sm" mb="xs">
                  Capabilities *
                </Text>
                <Text size="xs" c="dimmed" mb="md">
                  Specific capabilities granted by this role
                </Text>

                <Group mb="sm">
                  <TextInput
                    placeholder="e.g., prescribe-medication"
                    value={capabilityInput}
                    onChange={(e) => setCapabilityInput(e.currentTarget.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCapability();
                      }
                    }}
                    style={{ flex: 1 }}
                  />
                  <Button
                    leftSection={<IconPlus size={16} />}
                    onClick={handleAddCapability}
                  >
                    Add
                  </Button>
                </Group>

                <Group gap="xs">
                  {capabilities.map((capability) => (
                    <Badge
                      key={capability}
                      color="green"
                      variant="light"
                      rightSection={
                        <ActionIcon
                          size="xs"
                          color="green"
                          variant="transparent"
                          onClick={() =>
                            setCapabilities(
                              capabilities.filter((c) => c !== capability)
                            )
                          }
                        >
                          <IconX size={10} />
                        </ActionIcon>
                      }
                    >
                      {capability}
                    </Badge>
                  ))}
                  {capabilities.length === 0 && (
                    <Text size="sm" c="dimmed">
                      No capabilities added
                    </Text>
                  )}
                </Group>
              </Box>

              {/* Inherits From */}
              <Box>
                <Text fw={500} size="sm" mb="xs">
                  Inherits From (Optional)
                </Text>
                <Text size="xs" c="dimmed" mb="md">
                  Parent roles to inherit permissions from
                </Text>

                <Group mb="sm">
                  <TextInput
                    placeholder="e.g., base-user"
                    value={inheritInput}
                    onChange={(e) => setInheritInput(e.currentTarget.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddInherit();
                      }
                    }}
                    style={{ flex: 1 }}
                  />
                  <Button
                    leftSection={<IconPlus size={16} />}
                    onClick={handleAddInherit}
                  >
                    Add
                  </Button>
                </Group>

                <Group gap="xs">
                  {inheritsFrom.map((parent) => (
                    <Badge
                      key={parent}
                      color="orange"
                      variant="light"
                      rightSection={
                        <ActionIcon
                          size="xs"
                          color="orange"
                          variant="transparent"
                          onClick={() =>
                            setInheritsFrom(
                              inheritsFrom.filter((p) => p !== parent)
                            )
                          }
                        >
                          <IconX size={10} />
                        </ActionIcon>
                      }
                    >
                      {parent}
                    </Badge>
                  ))}
                  {inheritsFrom.length === 0 && (
                    <Text size="sm" c="dimmed">
                      No parent roles
                    </Text>
                  )}
                </Group>
              </Box>
            </Stack>
          </Card>
        )}

        {/* Preview Section */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Title order={4}>Preview</Title>

            <Paper p="md" withBorder>
              <Stack gap="sm">
                <Group>
                  <Text fw={500}>Template:</Text>
                  <Code>{templateName || '(not set)'}</Code>
                </Group>

                <Group>
                  <Text fw={500}>Display Name:</Text>
                  <Text>{displayName || '(not set)'}</Text>
                </Group>

                <Group>
                  <Text fw={500}>Category:</Text>
                  <Badge>{category || 'not set'}</Badge>
                </Group>

                {!isEditMode && (
                  <>
                    <Divider />

                    <Group>
                      <Text fw={500}>Role Name:</Text>
                      <Code>{roleName || '(not set)'}</Code>
                    </Group>

                    <Box>
                      <Text fw={500} mb="xs">
                        Permissions:
                      </Text>
                      <Text size="sm">
                        {scopes.length} scopes, {capabilities.length} capabilities
                      </Text>
                    </Box>
                  </>
                )}
              </Stack>
            </Paper>
          </Stack>
        </Card>
      </Stack>
    </Box>
  );
}
